import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Dropdown, { Option } from '@components/form/Dropdown.component';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Offcanvas } from 'react-bootstrap';
import { invalidateDimensions, ReportBuilderDimensionORM, useDimensions } from '@models/reportBuilder';
import PipelineNodeName, { PipelineNodeFieldName } from '@components/pipelineNodes/PipelineNodeName.component';
import { reorderList } from '@services/list.service';
import { Pane, PaneContent } from '@pages/PageStructure.component';
import { MetricsList, MetricItem, InfoSection } from './shared.styles';
import PipelineNodeColumnLineage from '@components/pipelineNodes/PipelineNodeColumnLineage.component';
import FullScreenTakeover from '@components/general/FullScreenTakeover.component';
import { savePipelineNode, useDateDimensionNode, usePipelineNodes } from '@stores/data.store';
import EditableText from '@components/general/EditableText.component';
import { PipelineNodeField } from '@models/pipelineNode';

interface DimensionSelectorProps {
    options: Option[];
    selected: string[];
    onChange: (newValue: string[]) => void;
    disabledOptionIds?: string[];
    disableMulti?: boolean;
    dateDimGranularityOnly?: boolean;
}

const DimensionSelector: React.FC<DimensionSelectorProps> = ({
    options,
    selected,
    onChange,
    disabledOptionIds,
    disableMulti = false,
    dateDimGranularityOnly = false,
}) => {
    const [dropdownValue, setDropdownValue] = useState('');
    const [infoDimensionId, setInfoDimensionId] = useState<string>();
    const [showLineage, setShowLineage] = useState(false);
    const dimensions = useDimensions();
    const nodes = usePipelineNodes();

    const datedim = useDateDimensionNode();

    const actualOptions = useMemo(() => {
        if (!datedim.data || !dimensions.data) {
            return [];
        }

        if (!dateDimGranularityOnly) {
            return options;
        }

        // If we want to specifically do granularity from the date dimension,
        // essentially we are overriding the options passed in with just the ones we want from the date dim.

        // We want the following taxonomic IDs:
        const taxonomicIds = [
            'PLB:::TPL:DEFAULT::NODE:CALENDAR::FIELD:DATE',
            'PLB:::TPL:DEFAULT::NODE:CALENDAR::FIELD:FIRST_DAY_OF_WEEK',
            'PLB:::TPL:DEFAULT::NODE:CALENDAR::FIELD:FIRST_DAY_OF_MONTH',
            'PLB:::TPL:DEFAULT::NODE:CALENDAR::FIELD:FIRST_DAY_OF_QUARTER',
            'PLB:::TPL:DEFAULT::NODE:CALENDAR::FIELD:FIRST_DAY_OF_YEAR',
        ];

        const fieldIds = datedim.data.fields.filter(f => taxonomicIds.includes(f.taxonomic_id)).map(f => f.id);

        return dimensions.data.filter(d => {
            if (d.pipeline_node_id !== datedim.data!.id as string) {
                return false;
            }
            return fieldIds.includes(d.field_id);
        }).map(d => ({
            value: d.id as string,
            label: d.name,
        }))
    }, [datedim.dataUpdatedAt, dateDimGranularityOnly, dimensions.dataUpdatedAt]);

    const handleAdd = (newValue: string) => {
        if (disableMulti) {
            // In single selection mode, replace the existing selection
            onChange([newValue]);
        } else {
            // In multi selection mode, add to the list if not already present
            if (!selected.includes(newValue)) {
                onChange([...selected, newValue]);
            }
        }
        setDropdownValue('');
    };

    const handleRemove = (valueToRemove: string) => {
        onChange(selected.filter(value => value !== valueToRemove));
    };

    const getOptionLabel = (value: string) => {
        return options.find(opt => opt.value === value)?.label || value;
    };

    const onDragEnd = (result: any) => {
        if (!result.destination || disableMulti) {
            return;
        }

        const newOrder = reorderList(
            selected,
            result.source.index,
            result.destination.index
        );
        onChange(newOrder);
    };

    const activeDimension = dimensions.data?.find(d => d.id === infoDimensionId);

    const [savingActiveDimension, setSavingActiveDimension] = useState(false);
    const changeActiveDimensionField = useCallback(async (fieldAttribute: keyof PipelineNodeField, newValue: any) => {
        if (!activeDimension || !nodes.data) {
            return;
        }

        const theNode = nodes.data.find(n => n.id === activeDimension.pipeline_node_id);
        if (!theNode) {
            return;
        }

        const theField = theNode.fields.find(f => f.id === activeDimension.field_id);
        if (!theField) {
            return;
        }
        setSavingActiveDimension(true);

        // @ts-ignore
        theField[fieldAttribute] = newValue;
        await savePipelineNode(theNode);
        invalidateDimensions();
        setSavingActiveDimension(false);

        
    }, [activeDimension, nodes.dataUpdatedAt]);

    const changeActiveDimensionDescription = useCallback(async (newValue: string) => {
        if (!activeDimension) {
            return;
        }
        setSavingActiveDimension(true);
        await ReportBuilderDimensionORM.patch(activeDimension.id as string, {
            description: newValue
        });
        invalidateDimensions();
        setSavingActiveDimension(false);
    }, [activeDimension]);

    return (
        <div>
            <Dropdown
                options={actualOptions}
                selected={dropdownValue}
                onChange={handleAdd}
                disabledOptionIds={[...disabledOptionIds || [], ...selected]}
                placeholder={disableMulti ? "Select a new dimension" : "Add dimension..."}
            />
            
            {selected.length > 0 && (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="dimensions-list">
                        {(provided) => (
                            <MetricsList
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {selected.map((value, index) => {
                                    const optionLabel = getOptionLabel(value);
                                    return (
                                        <Draggable 
                                            key={value} 
                                            draggableId={value} 
                                            index={index}
                                            isDragDisabled={disableMulti}
                                        >
                                            {(provided, snapshot) => (
                                                <MetricItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className={snapshot.isDragging ? 'dragging' : ''}
                                                >
                                                    {!disableMulti && <i className="mdi mdi-drag drag-handle"></i>}
                                                    <span className="metric-name overflow-ellipsis" title={optionLabel}>{optionLabel}</span>
                                                    <button
                                                        className="icon-button"
                                                        onClick={() => setInfoDimensionId(value)}
                                                    >
                                                        <i className="mdi mdi-information"></i>
                                                    </button>
                                                    <button 
                                                        className="icon-button delete-button"
                                                        onClick={() => handleRemove(value)}
                                                    >
                                                        <i className="mdi mdi-delete"></i>
                                                    </button>
                                                </MetricItem>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </MetricsList>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            <FullScreenTakeover
                show={showLineage}
                onHide={() => setShowLineage(false)}
            >
                {activeDimension && (
                    <PipelineNodeColumnLineage
                        pipelineNodeId={activeDimension.pipeline_node_id}
                        fieldId={activeDimension.field_id}
                    />
                )}
            </FullScreenTakeover>

            <Offcanvas 
                show={!!infoDimensionId} 
                onHide={() => setInfoDimensionId(undefined)} 
                placement="end"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="flex-1">Dimension Details</Offcanvas.Title>
                    {activeDimension && (
                        <>
                            {/* <button 
                                className="btn btn-light btn-sm me-2"
                                onClick={() => setShowLineage(true)}
                            >
                                <i className="mdi mdi-source-branch"></i> Lineage
                            </button> */}
                            {/* <Link 
                                to={`/dimension/${activeDimension.id}`}
                                className="btn btn-light btn-sm me-2"
                            >
                                <i className="mdi mdi-pencil"></i> Edit
                            </Link> */}
                        </>
                    )}
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Pane>
                        <PaneContent>
                            <div className="p-2">
                                {activeDimension && (
                                    <>
                                        <InfoSection>
                                            <h6>Name</h6>
                                            <EditableText
                                                value={activeDimension.name}
                                                onChange={changeActiveDimensionField.bind(null, 'dimension_label')}
                                            />
                                        </InfoSection>
                                        <InfoSection>
                                            <h6>Column Header</h6>
                                            <EditableText
                                                value={activeDimension.column_name}
                                                onChange={changeActiveDimensionField.bind(null, 'dimension_column_header')}
                                            />
                                        </InfoSection>

                                        <InfoSection>
                                            <h6>Description</h6>
                                            <EditableText
                                                value={activeDimension.description}
                                                onChange={changeActiveDimensionDescription}
                                                textarea
                                                emptyPlaceholder='Enter description...'
                                            />
                                        </InfoSection>

                                        <InfoSection>
                                            <h6>Source Table</h6>
                                            <p>
                                                <PipelineNodeName 
                                                    pipelineNodeId={activeDimension.pipeline_node_id} 
                                                />
                                            </p>
                                        </InfoSection>

                                        <InfoSection>
                                            <h6>Source Table Column Name</h6>
                                            <p>
                                                <PipelineNodeFieldName 
                                                    pipelineNodeId={activeDimension.pipeline_node_id} 
                                                    fieldId={activeDimension.field_id} 
                                                />
                                            </p>
                                        </InfoSection>
                                        {savingActiveDimension && <div className="text-muted font-13">
                                            <i className="mdi mdi-loading mdi-spin"></i> Saving...
                                        </div>}
                                    </>
                                )}
                            </div>
                        </PaneContent>
                    </Pane>
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default DimensionSelector;
